import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { t } from 'i18next';
import { isAxiosError } from 'axios';
import { useAppSelector } from '../../store/hooks';
import { selectMemberSession } from '../../store/slices/onboardingSlice';
import { Logger } from '../../config/logging/logger';
import { generateUuid } from '../RandomNumberHelper';
import {
  AgentBusinessProfile,
  COACH_TITLE_STATE_READY,
  CoachConversationSummary,
  CoachConversationTurn,
  CoachToken,
  createCoachToken,
  deleteCoachConversation,
  fetchCoachConversation,
  listCoachConversations,
  readyCoachConversationTitle,
  renameCoachConversation,
  upsertCoachConversation,
} from '../../api/coachAPI';

const logger = Logger.create('useAgentStream');

// Longest title we store, matching the backend schema's cap.
const MAX_TITLE_LENGTH = 80;

// eslint-disable-next-line no-shadow
export enum ChatRole {
  User = 'user',
  Assistant = 'assistant',
}

export interface ChatMessage {
  role: ChatRole;
  content: string;
  // Metadata for assistant messages (used for feedback).
  //
  // `runId` identifies the turn and is what feedback is keyed on: the gateway
  // reports it both live (`x-ep-run-id`) and on every turn it returns from
  // history, so a reopened conversation can tell which answers are already
  // rated. `assistantMessageId` is the gateway's id for the message itself - a
  // UUID live, a `msg-...` id from history - and only travels with the feedback
  // because the gateway's feedback API requires it.
  assistantMessageId?: string;
  runId?: string;
}

/**
 * Event types emitted by the AI SDK fullStream that we handle in the UI.
 */
// eslint-disable-next-line no-shadow
enum AgentStreamEventType {
  Start = 'start',
  ToolCall = 'tool-call',
  ToolResult = 'tool-result',
  TextDelta = 'text-delta',
  FinishStep = 'finish-step',
  Error = 'error',
}

/**
 * One event from the agent's AI SDK `fullStream`, surfaced to the UI. We only
 * model the fields the chat component needs; the platform emits more.
 */
interface AgentStreamEvent {
  type: AgentStreamEventType;
  text?: string;
  // The gateway's id for the answer, sent on `start`. Only a guarded turn
  // carries it - see the `Start` case in `applyEvent`.
  messageId?: string;
  response?: {
    id?: string;
  };
}

/**
 * Loading state of the member's chat history list.
 */
export type CoachHistoryStatus = 'idle' | 'loading' | 'ready' | 'error';

interface UseAgentStreamOptions {
  /**
   * Whether the chat is on screen. The Coach page keeps this hook mounted for
   * every tab and for members without beta access, so its state survives a tab
   * switch; this says when that chat is actually being shown.
   *
   * While it is true the hook keeps the member's conversation list loaded (once
   * on first show, then after each completed turn). While it is false nothing is
   * fetched, so we never mint an EPAT or call the gateway for someone who never
   * opens the chat.
   */
  isChatVisible?: boolean;
}

interface UseAgentStreamResult {
  messages: ChatMessage[];
  // True while the assistant is producing a response (the "thinking" window).
  isStreaming: boolean;
  // A short human-readable status of what the agent is doing, e.g. "Thinking…".
  statusText: string;
  error: string | null;
  sendMessage: (text: string, businessProfile?: AgentBusinessProfile) => Promise<void>;
  stop: () => void;
  // Metadata for the current conversation
  conversationId: string;
  agentId: string;
  // The member's past conversations, as listed by the gateway (unordered).
  conversations: CoachConversationSummary[];
  historyStatus: CoachHistoryStatus;
  // True while the turns of a past conversation are being fetched.
  isLoadingConversation: boolean;
  // Re-fetches the conversation list.
  refreshConversations: () => Promise<void>;
  // Abandons the on-screen chat and starts a fresh, empty one.
  startNewChat: () => void;
  // Loads a past conversation's turns into the transcript.
  openConversation: (conversationId: string) => Promise<void>;
  // Renames a past conversation. Applied to the list straight away and rolled
  // back if the save fails, in which case it rejects.
  renameConversation: (conversationId: string, title: string) => Promise<void>;
  // Soft-deletes a past conversation, dropping it from the list. Deleting the
  // chat on screen starts a new one. Rejects, leaving the list as it was, if the
  // delete fails.
  deleteConversation: (conversationId: string) => Promise<void>;
}

/**
 * Where the id of the chat on screen is remembered across a page load, per
 * member. `sessionStorage` rather than `localStorage` on purpose: this is
 * "carry on where I was", so it should survive a reload and a back-navigation
 * but not follow the member into a browser session days later - and a new tab
 * should open on a fresh chat rather than inheriting another tab's.
 */
const LAST_CONVERSATION_KEY_PREFIX = 'coach.lastConversationId';

const lastConversationKey = (memberId?: string) => (
  `${LAST_CONVERSATION_KEY_PREFIX}:${memberId ?? 'anonymous'}`
);

/**
 * Reads/writes the remembered conversation, tolerating a browser that refuses
 * storage (Safari private browsing throws on access). Failing to remember which
 * chat was open only costs the member the restore, so nothing is logged as an
 * error and no failure surfaces in the chat.
 */
function readLastConversationId(memberId?: string): string | null {
  try {
    return window.sessionStorage.getItem(lastConversationKey(memberId));
  } catch {
    return null;
  }
}

function writeLastConversationId(memberId: string | undefined, conversationId: string | null) {
  try {
    if (conversationId) {
      window.sessionStorage.setItem(lastConversationKey(memberId), conversationId);
    } else {
      window.sessionStorage.removeItem(lastConversationKey(memberId));
    }
  } catch {
    // No storage available - the chat works, it just will not be restored.
  }
}

/**
 * Flattens one history turn into the flat `ChatMessage` the transcript renders.
 * The gateway models a message as an array of parts; today only text parts are
 * produced, and anything else has nothing to show.
 */
function toChatMessage(turn: CoachConversationTurn): ChatMessage {
  const content = turn.parts
    .filter((part) => part.type === 'text' && part.text)
    .map((part) => part.text)
    .join('');

  return turn.role === ChatRole.Assistant ?
    {
      role: ChatRole.Assistant,
      content,
      assistantMessageId: turn.id,
      runId: turn.runId,
    } :
    { role: ChatRole.User, content };
}

/**
 * Drives a streaming conversation with the AI Coach agent.
 *
 * The browser streams from the EverPro Agents Gateway DIRECTLY. Before every
 * message we make sure we hold an unexpired EPAT (minted by ai-coach-service's
 * POST /coach/token) and re-mint if it has expired, then `fetch` the gateway
 * with the EPAT as a Bearer token. We use `fetch` + a ReadableStream reader
 * rather than axios/EventSource: axios (XHR) buffers the whole response, and
 * EventSource is GET-only and cannot POST a body. `fetch` gives true incremental
 * delivery so the user sees the agent "thinking" as events arrive.
 *
 * Why direct rather than proxying SSE through our own Lambda: a Lambda Function
 * URL silently buffers response streaming, so a proxy gained nothing. The
 * private signing key never leaves the backend; only the scoped EPAT does.
 *
 * We generate an opaque conversationId for each chat and send it on every turn
 * so the platform threads them together. The gateway persists the transcript
 * against it, so the same id can be replayed later: the hook also lists the
 * member's past conversations and can load one back into the transcript (SN-775).
 * tenant_id (the verified memberId) is baked into the EPAT by the backend - the
 * client sends no identity in the mint request.
 *
 * After each completed turn we also record the conversation's metadata - id,
 * title, start and last-active dates, deleted flag - to ai-coach-service
 * (SN-828). No message text is stored on our side; that stays in the gateway.
 * The point is to own this metadata so a member can later rename and delete
 * their conversations. Titles are the gateway's, generated asynchronously and
 * copied into our store once it reports them ready.
 *
 * The chat on screen is remembered for the browser session, so reloading the
 * page comes back to the conversation the member was in rather than to a new one
 * (SN-988).
 */
export function useAgentStream(
  { isChatVisible = false }: UseAgentStreamOptions = {},
): UseAgentStreamResult {
  const { memberId } = useAppSelector(selectMemberSession);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [statusText, setStatusText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [conversations, setConversations] = useState<CoachConversationSummary[]>([]);
  const [historyStatus, setHistoryStatus] = useState<CoachHistoryStatus>('idle');
  const [isLoadingConversation, setIsLoadingConversation] = useState(false);

  // Opaque id of the chat on screen: a freshly generated one for a new chat, or
  // the id of the past conversation the member reopened. Mirrored into a ref so
  // the streaming callback always sends the id that is current at send time.
  const [conversationId, setConversationId] = useState<string>(() => `conversation_${generateUuid()}`);
  const conversationIdRef = useRef<string>(conversationId);
  const abortControllerRef = useRef<AbortController | null>(null);
  // The current EPAT, cached across messages until it is about to expire.
  const tokenRef = useRef<CoachToken | null>(null);
  // The agent ID, set after first token mint
  const agentIdRef = useRef<string>('');
  // Guards against re-listing the history every time `isChatVisible` re-renders
  // true (e.g. on each tab switch back to the chat).
  const hasLoadedHistoryRef = useRef(false);
  // Guards the one-shot restore of the chat the member was in before a reload.
  const hasRestoredConversationRef = useRef(false);
  // Counts history refreshes so a slow one cannot overwrite the result of one
  // that started after it. The composer is not disabled while the list loads, so
  // the first load can still be in flight when the post-turn refresh goes out -
  // and if the older response landed last it would drop the brand new chat off
  // the list again.
  const historyRefreshSeqRef = useRef(0);
  // The latest rename per conversation, so a failed rename only rolls back if no
  // newer one has been made since.
  const renameSeqRef = useRef<Map<string, number>>(new Map());
  // Set when a refresh the chat needs is skipped because the chat is not on
  // screen. Picked up the next time it is shown, since hasLoadedHistoryRef would
  // otherwise leave the list stale for as long as the hook stays mounted.
  const isHistoryStaleRef = useRef(false);
  // Mirrors `conversations` so the streaming callback can read the latest list
  // (for the gateway's generated title) without `conversations` becoming a
  // dependency of sendMessage, which would rebuild it on every history refresh.
  const conversationsRef = useRef<CoachConversationSummary[]>(conversations);
  // When each conversation last had a turn in this session, keyed by id. Neither
  // the gateway's list nor our own record is guaranteed to report a just-finished
  // turn the moment the stream ends, so this is what keeps a chat the member has
  // just replied to showing today's date instead of the one it carried when they
  // reopened it. Only ever used to move a date forward - see withLocalActivity.
  const localLastMessageAtRef = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    conversationsRef.current = conversations;
  }, [conversations]);

  // Abort any in-flight gateway stream when the consumer unmounts, so we don't
  // keep reading the SSE response (or set state) after the component is gone.
  useEffect(() => () => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const stop = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsStreaming(false);
    setStatusText('');
  }, []);

  /**
   * Returns a valid EPAT, minting a fresh one if we have none or the current one
   * has expired. Verified before every message so a long-lived chat never sends
   * an expired token to the gateway.
   */
  const ensureToken = useCallback(
    async (): Promise<CoachToken> => {
      const { current } = tokenRef;
      if (current && current.expiresAtMs > Date.now()) {
        return current;
      }

      // No body: the backend derives the tenant from the authenticated session
      // (memberId). The client sends no identity here.
      const data = await createCoachToken();
      tokenRef.current = data;
      agentIdRef.current = data.agentId;
      return data;
    },
    [],
  );

  /**
   * Records the conversation in our own store (SN-828), so we own the metadata
   * the gateway does not: a member-editable title and a deleted flag.
   *
   * Metadata only - no transcript is sent. The gateway's generated title comes
   * along whenever we already know a ready one, which from a conversation's
   * second turn onward is the common case. It is not waited for: the gateway
   * generates titles asynchronously, so on a brand new conversation there is
   * nothing to send yet and the backend copies it across when it next lists the
   * member's history.
   *
   * Failures are swallowed on purpose. This is bookkeeping that runs after the
   * answer is already on screen, and the transcript itself lives in the gateway,
   * so a failed write must never surface as a chat error.
   */
  const recordConversation = useCallback(
    async (targetConversationId: string, title?: string) => {
      try {
        await upsertCoachConversation({
          conversationId: targetConversationId,
          agentId: agentIdRef.current,
          // Deliberately no startedAt/lastActiveAt: a turn just happened, so the
          // server's own clock is the right value for both.
          ...(title && { title: title.slice(0, MAX_TITLE_LENGTH) }),
        });
      } catch (recordError) {
        logger.error(Logger.format('Failed to record coach conversation', recordError));
      }
    },
    [],
  );

  /**
   * Carries the turns we watched happen into a freshly fetched list, so a chat
   * the member just replied to keeps the date of that reply.
   *
   * Only ever moves a date forward: as soon as the server reports the turn (or a
   * later one), its value wins and ours is ignored. Nothing is dropped when we
   * know of no turn for a conversation.
   */
  const withLocalActivity = useCallback(
    (listed: CoachConversationSummary[]): CoachConversationSummary[] => listed.map((summary) => {
      const localLastMessageAt = localLastMessageAtRef.current.get(summary.conversationId);
      return localLastMessageAt && localLastMessageAt > summary.lastMessageAt ?
        { ...summary, lastMessageAt: localLastMessageAt } :
        summary;
    }),
    [],
  );

  const refreshConversations = useCallback(async () => {
    historyRefreshSeqRef.current += 1;
    const refreshSeq = historyRefreshSeqRef.current;
    // False once a later refresh has started: this one's answer is stale, and
    // applying it would undo the newer list.
    const isLatestRefresh = () => historyRefreshSeqRef.current === refreshSeq;

    try {
      setHistoryStatus((current) => (current === 'ready' ? current : 'loading'));
      // The backend has already dropped deleted chats, applied renames and
      // fallen back to our own records if the gateway's list failed (SN-825).
      const listed = await listCoachConversations();
      if (!isLatestRefresh()) {
        return;
      }
      setConversations(withLocalActivity(listed));
      setHistoryStatus('ready');
    } catch (listError) {
      logger.error(Logger.format('Failed to list coach conversations', listError));
      if (isLatestRefresh()) {
        setHistoryStatus('error');
      }
    }
  }, [withLocalActivity]);

  // Load the history the first time the chat is actually shown - and again on a
  // later showing if a turn finished while it was away, which is the one case
  // that leaves the list we already have out of date.
  useEffect(() => {
    if (!isChatVisible || (hasLoadedHistoryRef.current && !isHistoryStaleRef.current)) {
      return;
    }
    hasLoadedHistoryRef.current = true;
    isHistoryStaleRef.current = false;
    refreshConversations();
  }, [isChatVisible, refreshConversations]);

  const startNewChat = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    const newConversationId = `conversation_${generateUuid()}`;
    conversationIdRef.current = newConversationId;
    // Nothing to come back to yet: an unsent chat has no transcript to restore,
    // so a reload from here lands on a new chat rather than the one before it.
    writeLastConversationId(memberId, null);
    setConversationId(newConversationId);
    setMessages([]);
    setError(null);
    setIsStreaming(false);
    setStatusText('');
  }, [memberId]);

  /**
   * Replaces the transcript with a past conversation's turns. The chat on screen
   * is abandoned rather than saved separately - the gateway has already persisted
   * whatever was sent, so it is reachable from the history list.
   */
  const openConversation = useCallback(async (targetConversationId: string) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    conversationIdRef.current = targetConversationId;
    // Remembered so a reload comes back to this chat rather than to a new one.
    writeLastConversationId(memberId, targetConversationId);
    setConversationId(targetConversationId);
    setIsStreaming(false);
    setStatusText('');
    setError(null);
    // Drop the outgoing conversation's turns up front. Keeping them until the
    // new ones arrive would leave the previous chat's transcript - and the
    // feedback state that belongs to it - on screen under the new chat's title.
    setMessages([]);
    setIsLoadingConversation(true);

    try {
      const turns = await fetchCoachConversation(await ensureToken(), targetConversationId);
      // Ignore a response that lost the race with a later selection.
      if (conversationIdRef.current !== targetConversationId) {
        return;
      }
      setMessages(turns.map(toChatMessage));
    } catch (historyError) {
      logger.error(Logger.format('Failed to load coach conversation', historyError));
      if (conversationIdRef.current === targetConversationId) {
        setMessages([]);
        setError(t('error.coachHistoryUnavailable'));
      }
    } finally {
      if (conversationIdRef.current === targetConversationId) {
        setIsLoadingConversation(false);
      }
    }
  }, [ensureToken, memberId]);

  /**
   * Runs a rename or delete against our store, indexing the conversation first
   * if it turns out we never recorded it.
   *
   * The backend only renames or deletes a row it already has, and a
   * conversation can be listed by the gateway without one - an untitled chat
   * whose per-turn write failed, or one from before we kept an index. A 404 is
   * answered by recording it with the gateway's own dates and trying once more.
   */
  const withIndexedConversation = useCallback(
    async (targetConversationId: string, action: () => Promise<unknown>) => {
      try {
        await action();
      } catch (actionError) {
        if (!isAxiosError(actionError) || actionError.response?.status !== 404) {
          throw actionError;
        }
        const summary = conversations.find(
          (candidate) => candidate.conversationId === targetConversationId,
        );
        // The history no longer goes through the gateway, so a token - and with
        // it the agent id - may not have been fetched yet.
        await ensureToken();
        await upsertCoachConversation({
          conversationId: targetConversationId,
          agentId: agentIdRef.current,
          ...(summary && {
            startedAt: summary.startedAt,
            lastActiveAt: summary.lastMessageAt,
          }),
        });
        await action();
      }
    },
    [conversations, ensureToken],
  );

  /**
   * Renames a conversation, showing the new title straight away (SN-825).
   *
   * Any refresh already in flight is superseded: it read our store before the
   * rename landed, so applying it would put the old title back. A failure only
   * restores the old title when no newer rename of the chat has been made.
   */
  const renameConversation = useCallback(async (targetConversationId: string, title: string) => {
    const trimmed = title.trim().slice(0, MAX_TITLE_LENGTH);
    const previous = conversations.find(
      (summary) => summary.conversationId === targetConversationId,
    );
    if (!trimmed || !previous) {
      return;
    }

    historyRefreshSeqRef.current += 1;
    const renameSeq = (renameSeqRef.current.get(targetConversationId) ?? 0) + 1;
    renameSeqRef.current.set(targetConversationId, renameSeq);
    const renameOf = (summary: CoachConversationSummary, next: CoachConversationSummary) => (
      summary.conversationId === targetConversationId ? next : summary
    );
    setConversations((current) => current.map((summary) => renameOf(
      summary,
      { ...summary, title: trimmed, titleState: COACH_TITLE_STATE_READY },
    )));

    try {
      await withIndexedConversation(
        targetConversationId,
        () => renameCoachConversation(targetConversationId, trimmed),
      );
    } catch (renameError) {
      logger.error(Logger.format('Failed to rename coach conversation', renameError));
      if (renameSeqRef.current.get(targetConversationId) === renameSeq) {
        setConversations((current) => current.map((summary) => renameOf(summary, previous)));
      }
      throw renameError;
    }
  }, [conversations, withIndexedConversation]);

  /**
   * Soft-deletes a conversation (SN-825). Unlike a rename this waits for the
   * backend before touching the list: the member confirmed it in a dialog that
   * stays open until it is done, and a chat that vanished then came back on a
   * failure would be more confusing than a short wait.
   *
   * Deleting the chat on screen leaves nothing to show, so it starts a new one -
   * which also forgets it as the chat to come back to after a reload.
   */
  const deleteConversation = useCallback(async (targetConversationId: string) => {
    try {
      await withIndexedConversation(
        targetConversationId,
        () => deleteCoachConversation(targetConversationId),
      );
    } catch (deleteError) {
      logger.error(Logger.format('Failed to delete coach conversation', deleteError));
      throw deleteError;
    }

    historyRefreshSeqRef.current += 1;
    setConversations((current) => current.filter(
      (summary) => summary.conversationId !== targetConversationId,
    ));
    if (conversationIdRef.current === targetConversationId) {
      startNewChat();
    }
  }, [startNewChat, withIndexedConversation]);

  /**
   * Puts the member back in the chat they were in before the page reloaded, so a
   * refresh mid-conversation is not answered with an empty welcome banner.
   *
   * Waits for the history list: the remembered id is only reopened when the list
   * still has it, which is what stops a reload landing on the error state for a
   * chat that has since been deleted, or that belongs to a member who has since
   * signed out of this tab. Runs once - a member who has already started typing,
   * or who reached the chat and moved on themselves, is left where they are.
   */
  useEffect(() => {
    if (!isChatVisible || hasRestoredConversationRef.current || historyStatus !== 'ready') {
      return;
    }
    hasRestoredConversationRef.current = true;

    const lastConversationId = readLastConversationId(memberId);
    if (!lastConversationId) {
      return;
    }

    const isRestorable = lastConversationId !== conversationIdRef.current &&
      messages.length === 0 &&
      !isStreaming &&
      conversations.some((summary) => summary.conversationId === lastConversationId);

    if (isRestorable) {
      openConversation(lastConversationId);
    }
  }, [
    conversations,
    historyStatus,
    isChatVisible,
    isStreaming,
    memberId,
    messages.length,
    openConversation,
  ]);

  // Applies one parsed agent event to the in-progress assistant message.
  const applyEvent = useCallback((event: AgentStreamEvent, runId?: string) => {
    switch (event.type) {
      case AgentStreamEventType.Start:
        // A turn the guardrail answers is decided before the model runs, so its
        // `finish-step` carries no `response` and the id feedback needs would
        // never arrive. The gateway sends that id here instead, as `messageId`.
        // A normal answer's `start` carries none (its id comes on `finish-step`
        // below), hence the guard: without it every unguarded turn would clear
        // the id it is about to be given.
        if (event.messageId) {
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === ChatRole.Assistant) {
              next[next.length - 1] = {
                ...last,
                assistantMessageId: event.messageId,
                runId: runId ?? last.runId,
              };
            }
            return next;
          });
        }
        break;
      case AgentStreamEventType.ToolCall:
        setStatusText(`${t('coach.status.lookingUp')}`);
        break;
      case AgentStreamEventType.ToolResult:
        setStatusText(`${t('coach.status.thinking')}`);
        break;
      case AgentStreamEventType.TextDelta:
        if (event.text) {
          setStatusText(`${t('coach.status.typing')}`);
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === ChatRole.Assistant) {
              next[next.length - 1] = { ...last, content: last.content + event.text };
            }
            return next;
          });
        }
        break;
      case AgentStreamEventType.FinishStep:
        // Tag the finished answer with what feedback needs: the run id (which
        // owns the whole turn) and the gateway's message id. The run id is kept
        // even if the step carries no message id, since it is the one the
        // feedback record is keyed on.
        if (runId || event.response?.id) {
          setMessages((prev) => {
            const next = [...prev];
            const last = next[next.length - 1];
            if (last?.role === ChatRole.Assistant) {
              next[next.length - 1] = {
                ...last,
                assistantMessageId: event.response?.id ?? last.assistantMessageId,
                runId: runId ?? last.runId,
              };
            }
            return next;
          });
        }
        break;
      case AgentStreamEventType.Error:
        setError(t('error.coachGeneric'));
        break;
      default:
        break;
    }
  }, []);

  const sendMessage = useCallback(
    async (text: string, businessProfile?: AgentBusinessProfile) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) {
        return;
      }

      if (!memberId) {
        setError(t('error.coachUnavailable'));
        return;
      }

      setError(null);
      setIsStreaming(true);
      setStatusText(`${t('coach.status.thinking')}`);
      // Append the user message and an empty assistant message to fill in.
      setMessages((prev) => [
        ...prev,
        { role: ChatRole.User, content: trimmed },
        { role: ChatRole.Assistant, content: '' },
      ]);

      const abortController = new AbortController();
      abortControllerRef.current = abortController;
      // Pinned for the whole turn so a mid-stream conversation switch cannot
      // retarget this request or its follow-up history refresh.
      const turnConversationId = conversationIdRef.current;
      let turnCompleted = false;

      try {
        // Verify the EPAT is still valid before sending; re-mint if not.
        const coachToken = await ensureToken();

        const response = await fetch(coachToken.gatewayUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${coachToken.token}`,
          },
          body: JSON.stringify({
            agentId: coachToken.agentId,
            conversationId: turnConversationId,
            stream: true,
            message: {
              role: ChatRole.User,
              parts: [{ type: 'text', text: trimmed }],
            },
            ...(businessProfile && { params: { businessProfile } }),
          }),
          signal: abortController.signal,
        });

        if (!response.ok || !response.body) {
          throw new Error(`Coach stream failed with status ${response.status}`);
        }

        // The run id the gateway assigns to this agent invocation, sent back as
        // `x-ep-run-id` (CORS-exposed, so readable here). It is what the platform
        // correlates feedback to, so never fall back to `x-request-id` - that is
        // the HTTP request id and would silently tag feedback with an id that
        // matches no run.
        const runId = response.headers.get('x-ep-run-id') ||
          response.headers.get('x-run-id') ||
          undefined;

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        // Read the SSE stream incrementally. A chunk may contain a partial
        // frame or several frames, so we buffer and split on the blank line.
        for (;;) {
          // eslint-disable-next-line no-await-in-loop
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          // Split on the blank line between SSE frames. The dev gateway emits
          // LF-only today, but CRLF is valid per the SSE spec (and a proxy/CDN
          // could rewrite endings), so accept both \n\n and \r\n\r\n.
          const frames = buffer.split(/\r?\n\r?\n/);
          buffer = frames.pop() ?? '';

          for (const frame of frames) {
            for (const line of frame.split(/\r?\n/)) {
              if (!line.startsWith('data:')) {
                // eslint-disable-next-line no-continue
                continue;
              }
              const payload = line.slice(5).trim();
              if (!payload) {
                // eslint-disable-next-line no-continue
                continue;
              }
              try {
                applyEvent(JSON.parse(payload) as AgentStreamEvent, runId);
              } catch (parseError) {
                logger.warn(Logger.format('Failed to parse coach event', parseError));
              }
            }
          }
        }

        turnCompleted = true;
      } catch (streamError) {
        if ((streamError as Error).name !== 'AbortError') {
          logger.error(Logger.format('Coach stream error', streamError));
          setError(t('error.coachGeneric'));
        }
      } finally {
        setIsStreaming(false);
        setStatusText('');
        abortControllerRef.current = null;

        // Record the turn in our own store: creates the row on the first turn and
        // bumps lastActiveAt on every turn, carrying the gateway's title whenever
        // we already know a ready one. A brand new conversation has none yet
        // (the gateway generates them asynchronously), so the refresh below - or
        // the next turn - is what seeds it. Keyed on the pinned turn id, so a
        // mid-stream conversation switch cannot touch the wrong record. Not
        // awaited, and it swallows its own failures - see recordConversation.
        //
        // Deliberately outside the isChatVisible / current-conversation guard
        // below: unlike the history refresh, which only feeds what is on screen,
        // this write must happen for a completed turn even if the member has
        // since switched tab or opened another conversation.
        // Move the conversation the member just replied to up to now, so a chat
        // they reopened carries the date of the message they have just sent rather
        // than the one it had when they opened it. Remembered as well as applied:
        // the refresh below can answer with a list that does not show the turn
        // yet, and withLocalActivity is what stops that putting the old date back.
        // Only conversations already listed are touched - a brand new chat has no
        // row yet, and the refresh is what adds it.
        if (turnCompleted) {
          // The chat now has a transcript the gateway can replay, so it is worth
          // coming back to after a reload. Written here rather than on send: a
          // turn that never completed has nothing to restore.
          writeLastConversationId(memberId, turnConversationId);

          const turnEndedAt = new Date().toISOString();
          localLastMessageAtRef.current.set(turnConversationId, turnEndedAt);
          setConversations((current) => current.map((summary) => (
            summary.conversationId === turnConversationId ?
              { ...summary, lastMessageAt: turnEndedAt } :
              summary
          )));
        }

        const recorded = turnCompleted ?
          recordConversation(
            turnConversationId,
            readyCoachConversationTitle(
              conversationsRef.current.find(
                (summary) => summary.conversationId === turnConversationId,
              ),
            ),
          ) :
          null;

        // The turn is now on the gateway, so re-list to pick the conversation up
        // (a brand new chat only becomes a history entry after its first turn)
        // and to move it to the top of the list. Deliberately not awaited: the
        // composer should come back the moment the stream ends, and
        // refreshConversations handles its own failures.
        //
        // Chained onto the write above rather than fired alongside it: the write
        // is what bumps lastActiveAt, and a list fetched before it landed still
        // carries the old timestamp. That is what left a chat the member had just
        // replied to sitting under YESTERDAY with yesterday's date until something
        // else refreshed the list. recordConversation swallows its own failures,
        // so this still runs when the write fails - the list is simply unchanged.
        if (recorded && conversationIdRef.current === turnConversationId) {
          if (isChatVisible) {
            recorded.then(() => refreshConversations());
          } else {
            // Nothing to fetch for a list nobody is looking at, but it is now out
            // of date - a brand new chat has no row in it yet, and the local
            // timestamp above cannot add one. Left for the next time the chat is
            // shown rather than dropped, which used to leave the member coming
            // back to a history still missing the chat they had just had.
            isHistoryStaleRef.current = true;
          }
        }
      }
    },
    [
      applyEvent,
      ensureToken,
      isChatVisible,
      isStreaming,
      memberId,
      recordConversation,
      refreshConversations,
    ],
  );

  return {
    messages,
    isStreaming,
    statusText,
    error,
    sendMessage,
    stop,
    conversationId,
    agentId: agentIdRef.current,
    conversations,
    historyStatus,
    isLoadingConversation,
    refreshConversations,
    startNewChat,
    openConversation,
    renameConversation,
    deleteConversation,
  };
}
